
import type { Practitioner, NewChat, NewMessage } from '../common'
import type { ChatMessagesPage, ChatPage } from '../queries'
import type { InfiniteData, QueryKey } from '@tanstack/react-query'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import http from '../../../lib/http'
import { useAppDispatch } from '../../../store'
import { APPEND_MESSAGE } from '../../chat/actions'
import { invariant, type Attachment, type Message } from '../common'
import { getChatMessagesQueryKey, getChatsQueryKey } from '../queries'

export interface MessagePayloadWithAttachment {
  text?: string
  file: File
}

export interface MessagePayloadWithoutAttachment {
  text: string
}

export type MessagePayload =
  | MessagePayloadWithAttachment
  | MessagePayloadWithoutAttachment

export interface FileUploadResponse {
  files?: {
    file0?: {
      filename: string
      id: string
      mime_type: string
      size: null
    }
  }
}

interface PublishPayload {
  message: Pick<Message, 'body'> & {
    attachments?: Array<Pick<Attachment, 'id' | 'type' | 'filename'>>
  }
}

interface Context {
  previousSelectedChat?: NewChat
  previousChats?: Array<[QueryKey, InfiniteData<ChatPage>]>
  previousMessages?: Array<[QueryKey, InfiniteData<ChatMessagesPage>]>
}

interface UseSendMessageMutationOptions {
  userId?: string
  patientId?: string
  chatId?: string
  participantId?: string
  clientName?: string
}


export function useSendMessageMutation (options: UseSendMessageMutationOptions) {
  let { userId, patientId, chatId, participantId, clientName } = options
  let queryClient = useQueryClient()
  let dispatch = useAppDispatch()

  return useMutation<unknown, unknown, MessagePayload, Context>({
    async mutationFn (message) {
      invariant(typeof userId === 'string' && userId.length > 0, 'userId is required')
      invariant(typeof patientId === 'string' && patientId.length > 0, 'patientId is required')
      invariant(typeof chatId === 'string' && chatId.length > 0, 'chatId is required')
      invariant(typeof participantId === 'string' && participantId.length > 0, 'participantId is required')

      let uploadId: string | undefined

      if ('file' in message && message.file) {
        let formData = new FormData()
        formData.append('file0', message.file, message.file.name)

        let response: FileUploadResponse = await http.postRequest('/documents', formData, { clientName })
        uploadId = response.files?.file0?.id
      }

      let payload: PublishPayload = {
        message: {
          body: (message.text?.trim() ?? '') || null
        }
      }

      if (
        typeof uploadId === 'string' &&
        'file' in message &&
        message.file
      ) {
        payload.message.attachments = [
          {
            type: 'aidbox',
            id: uploadId,
            filename: message.file.name
          }
        ]
      }

      let url = `/patients/${patientId}/chats/${chatId}/publish?participant=${participantId}`
      return await http.postRequest(url, payload, { clientName })
    },

    // @ts-expect-error // it's okay
    async onMutate (payload) {
      let chatsQueryKey = getChatsQueryKey({ userId })
      let messagesQueryKey = getChatMessagesQueryKey({ chatId })
      let selectedChatQueryKey = [ 'chat', { id: chatId, userId }]
      let selectedChatParticipantsQueryKey = [ 'chat-participants', { chatId, userId }]

      await Promise.all([
        queryClient.cancelQueries({ queryKey: chatsQueryKey, exact: false }),
        queryClient.cancelQueries({ queryKey: messagesQueryKey, exact: false }),
        queryClient.cancelQueries({ queryKey: selectedChatQueryKey, exact: false })
      ])

      let selectedChat = queryClient.getQueryData<NewChat>(selectedChatQueryKey)
      let previousChats = queryClient.getQueriesData<InfiniteData<ChatPage>>({ queryKey: chatsQueryKey, exact: false })
      let previousMessages = queryClient.getQueriesData<InfiniteData<ChatMessagesPage>>({ queryKey: messagesQueryKey, exact: false })
      let selectedChatParticipants = queryClient.getQueryData<Practitioner[]>(selectedChatParticipantsQueryKey)

      let message: Message = {
        id: `local-${Date.now()}`,
        body: (payload.text?.trim() ?? '') || null,
        author: participantId,
        chat_id: chatId,
        // TODO: fix when attachments are implemented
        attachments: [],
        created_at: new Date().toISOString(),
        tags: [],

        // @ts-expect-error // It's fine
        concierge: (selectedChat?.concierge ?? false)
          ? selectedChatParticipants?.find(p => p.id === userId) ?? {
            id: userId as string,
            name: []
          }
          : undefined
      }

      let newMessage: NewMessage = {
        id: `local-${Date.now()}`,
        body: (payload.text?.trim() ?? '') || null,
        author: participantId,
        // TODO: fix when attachments are implemented
        createdAt: new Date().toISOString(),
        tags: [],

        // @ts-expect-error // It's fine
        concierge: (selectedChat?.concierge ?? false)
          ? selectedChatParticipants?.find(p => p.id === userId) ?? {
            id: userId as string,
            name: []
          }
          : undefined
      }

      queryClient.setQueryData<NewChat>(selectedChatQueryKey, current => {
        if (!current) {
          return
        }

        return {
          ...current,
          messageCount: (current.messageCount ?? 0) + 1,
          lastMessage: newMessage
        }
      })

      queryClient.setQueriesData<InfiniteData<ChatPage>>({ queryKey: chatsQueryKey, exact: false }, current => {
        return {
          ...current,
          pages: current?.pages.map(
            page => page.map(
              chat => ({
                ...chat,
                messageCount: chat.id === chatId
                  ? (chat.messageCount ?? 0) + 1
                  : chat.messageCount,
                lastMessage: chat.id === chatId
                  ? newMessage
                  : chat.lastMessage
              })
            )
          ) ?? [],
          pageParams: current?.pageParams ?? []
        }
      })

      queryClient.setQueriesData<InfiniteData<ChatMessagesPage>>({ queryKey: messagesQueryKey, exact: false }, current => {
        return {
          ...current,
          pages: [
            [
              {
                ...message,
                offset: (current?.pages?.[0]?.[0]?.offset ?? 0) + 1
              },
              ...(current?.pages?.[0] ?? [])
            ],
            ...(current?.pages?.slice(1) ?? [])
          ],
          pageParams: current?.pageParams ?? []
        }
      })

      return {
        previousChats,
        previousMessages,
        previousSelectedChat: selectedChat
      }
    },

    async onSettled (data, error, payload, context) {
      let chatsQueryKey = getChatsQueryKey({ userId })
      let messagesQueryKey = getChatMessagesQueryKey({ userId, chatId })
      let selectedChatQuery = [ 'chat', { id: chatId, userId }]

      await queryClient.invalidateQueries({ queryKey: messagesQueryKey, exact: false })
      await queryClient.invalidateQueries({ queryKey: chatsQueryKey, exact: false })
      await queryClient.invalidateQueries({ queryKey: selectedChatQuery, exact: false })

      dispatch({
        type: APPEND_MESSAGE,
        chatId,
        message: data
      })
    },

    async onError (err, payload, context) {
      void err

      let selectedChatQuery = [ 'chat', { id: chatId, userId }]

      toast.error('Your message was not sent', {
        duration: 5000,
        position: 'bottom-center'
      })

      if (context?.previousSelectedChat) {
        queryClient.setQueryData(selectedChatQuery, context.previousSelectedChat)
      }

      if (context?.previousMessages) {
        for (let [ queryKey, data ] of context.previousMessages) {
          queryClient.setQueryData(queryKey, data)
        }
      }

      if (context?.previousChats) {
        for (let [ queryKey, data ] of context.previousChats) {
          queryClient.setQueryData(queryKey, data)
        }
      }
    }
  })
}
